import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export default function Footer() {
  const { lang } = useLanguage();
  const currentYear = new Date().getFullYear();

  const isEn = lang === 'en';
  const isJa = lang === 'ja';

  const footerLinks =
    isEn
      ? {
          company: { title: 'Company', links: [{ label: 'About Us', path: '/about' }, { label: 'Core Technology', path: '/technology' }, { label: 'Sustainability & Carbon Impact', path: '/sustainability' }] },
          products: { title: 'Products & Services', links: [{ label: 'Products & Materials', path: '/products' }, { label: 'Applications & Solutions', path: '/solutions' }, { label: 'Partnership & Distribution', path: '/partnership' }] },
        }
      : isJa
      ? {
          company: { title: '会社情報', links: [{ label: '会社概要', path: '/about' }, { label: 'コア技術', path: '/technology' }, { label: 'サステナビリティと炭素効果', path: '/sustainability' }] },
          products: { title: '製品・サービス', links: [{ label: '製品・材料', path: '/products' }, { label: '応用シーンとソリューション', path: '/solutions' }, { label: 'パートナーシップ・販売代理', path: '/partnership' }] },
        }
      : {
          company: { title: '公司', links: [{ label: '關於我們', path: '/about' }, { label: '核心技術', path: '/technology' }, { label: '永續與碳效益', path: '/sustainability' }] },
          products: { title: '產品與服務', links: [{ label: '產品與材料', path: '/products' }, { label: '應用場景與解決方案', path: '/solutions' }, { label: '合作模式與經銷夥伴', path: '/partnership' }] },
        };

  const logoMain = isEn ? 'PolyQ Applied Materials' : isJa ? '聚量応用材料' : '聚量應用材料';
  const logoSub = isEn ? 'PolyQ Applied Materials Co., Ltd.' : isJa ? '聚量応用材料株式会社' : 'JuuLiang Applied Materials';
  const tagline = isEn
    ? 'Leveraging PQernel® nano-technology to upgrade waste plastics into high-value recycled materials, driving circular economy and low-carbon manufacturing.'
    : isJa
    ? 'PQernel®ナノテクノロジーにより廃プラスチックを高付加価値の再生材料へとアップグレードし、サーキュラーエコノミーと低炭素製造を推進します。'
    : '以 PQernel® 奈米技術，協助廢棄塑膠升級為高價值再生材料，推動循環經濟與低碳製造。';
  const contactTitle = isEn ? 'Contact Info' : isJa ? '連絡先' : '聯絡資訊';
  const location = isEn ? '12, Dafu Rd., Shengang Dist., Taichung City 429, Taiwan' : isJa ? '台湾台中市神岡区大富路12号 429' : '429台中市神岡區大富路12號';
  const circularLabel = isEn ? 'Circular Economy · Low-Carbon Manufacturing' : isJa ? 'サーキュラーエコノミー・低炭素製造' : '循環經濟 · 低碳製造';
  const copyright = isEn
    ? `© ${currentYear} PolyQ Applied Materials Co., Ltd. All rights reserved.`
    : isJa
    ? `© ${currentYear} 聚量応用材料株式会社. All rights reserved.`
    : `© ${currentYear} 聚量應用材料股份有限公司. All rights reserved.`;
  const trademark = isEn ? 'PQernel® is a registered trademark.' : isJa ? 'PQernel®は登録商標です。' : 'PQernel® 為本公司註冊商標';

  return (
    <footer className="bg-[#F0F4F8] border-t border-neutral-border">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4 cursor-pointer">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-brand-green to-brand-blue flex-shrink-0">
                <i className="ri-leaf-line text-white text-lg"></i>
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-[0.9375rem] font-bold text-neutral-dark tracking-tight">{logoMain}</span>
                <span className="text-[0.6875rem] text-neutral-mid tracking-wide">{logoSub}</span>
              </div>
            </Link>
            <p className="text-[0.9375rem] text-neutral-mid leading-relaxed mb-5">{tagline}</p>
            <div className="flex items-center gap-3">
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-neutral-border text-neutral-mid hover:text-brand-green hover:border-brand-green transition-colors cursor-pointer" aria-label="LinkedIn">
                <i className="ri-linkedin-line text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-neutral-border text-neutral-mid hover:text-brand-green hover:border-brand-green transition-colors cursor-pointer" aria-label="Facebook">
                <i className="ri-facebook-line text-lg"></i>
              </a>
              <a href="mailto:chang.chunghung@polyquantummaterials.com" className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-neutral-border text-neutral-mid hover:text-brand-green hover:border-brand-green transition-colors cursor-pointer" aria-label="Email">
                <i className="ri-mail-line text-lg"></i>
              </a>
            </div>
          </div>

          {/* Nav Columns */}
          {Object.values(footerLinks).map((section) => (
            <div key={section.title}>
              <h4 className="text-[0.9375rem] font-semibold text-neutral-dark mb-4 tracking-wide">
                <a id={`footer-${section.title}`}>{section.title}</a>
              </h4>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-[0.9375rem] text-neutral-mid hover:text-brand-green transition-colors cursor-pointer">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Info */}
          <div>
            <h4 className="text-[0.9375rem] font-semibold text-neutral-dark mb-4 tracking-wide">
              <a id="footer-contact-info">{contactTitle}</a>
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-[0.9375rem] text-neutral-mid">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-brand-green"></i>
                </div>
                <span className="break-all leading-snug">{location}</span>
              </li>
              <li className="flex items-start gap-2 text-[0.9375rem] text-neutral-mid">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-phone-line text-brand-green"></i>
                </div>
                <a href="tel:+886958471138" className="hover:text-brand-green transition-colors cursor-pointer whitespace-nowrap">
                  +886 958-471-138
                </a>
              </li>
              <li className="flex items-start gap-2 text-[0.9375rem] text-neutral-mid">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-mail-line text-brand-green"></i>
                </div>
                <a href="mailto:chang.chunghung@polyquantummaterials.com" className="hover:text-brand-green transition-colors cursor-pointer break-all leading-snug">
                  chang.chunghung@polyquantummaterials.com
                </a>
              </li>
              <li className="flex items-start gap-2 text-[0.9375rem] text-neutral-mid">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-recycle-line text-brand-green"></i>
                </div>
                <span>{circularLabel}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-neutral-border bg-[#E8EDF3]">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[1rem] text-neutral-mid text-center sm:text-left">{copyright}</p>
            <div className="flex items-center gap-4">
              <span className="text-[1rem] text-neutral-mid">{trademark}</span>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-neutral-border/50">
            <a
              href="https://vpivip.com/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="block text-center text-[0.6875rem] md:text-[0.75rem] text-neutral-mid/50 hover:text-neutral-mid/65 transition-colors cursor-pointer"
            >
              品牌官網設計｜天地人網整合行銷
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
